import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PiercingZoneGuide } from '../utils/piercingZoneGuides';
import { getPiercingGuideHotspot } from '../utils/piercingGuideHotspots';

interface PiercingZoneGuidePanelProps {
  guide: PiercingZoneGuide;
  /** Currently selected piercing type id (persistent highlight). */
  selectedPiercingTypeId?: string;
  /** Selected piercing type name (official catalog sales reference). */
  selectedPiercingTypeName?: string;
  /** Hovered / keyboard-focused piercing type id (preview highlight). */
  previewPiercingTypeId?: string;
  /** Hovered piercing type name. */
  previewPiercingTypeName?: string;
  /** Extra classes for the outer wrapper (e.g. sticky on desktop). */
  className?: string;
}

export function PiercingZoneGuidePanel({
  guide,
  selectedPiercingTypeId = '',
  selectedPiercingTypeName = '',
  previewPiercingTypeId = '',
  previewPiercingTypeName = '',
  className = '',
}: PiercingZoneGuidePanelProps) {
  const { t } = useTranslation();

  const activeId = previewPiercingTypeId || selectedPiercingTypeId;
  const activeName = previewPiercingTypeName || selectedPiercingTypeName;
  const hotspot =
    getPiercingGuideHotspot(activeName) || getPiercingGuideHotspot(activeId);
  const isPreviewOnly =
    Boolean(previewPiercingTypeId || previewPiercingTypeName) &&
    previewPiercingTypeId !== selectedPiercingTypeId;

  return (
    <figure
      className={`fo-panel rounded-xl border border-gray-200 bg-gray-50 overflow-hidden shadow-sm ${className}`}
    >
      <div className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4">
        <figcaption className="text-sm font-semibold text-textPrimary text-center">
          {t(`piercingBooking.zoneGuide.${guide.titleKey}`)}
        </figcaption>
        <p className="mt-1 text-xs text-gray-500 text-center leading-snug">
          {t(`piercingBooking.zoneGuide.${guide.captionKey}`)}
        </p>
      </div>
      <div className="px-2 pb-2 sm:px-3 sm:pb-3">
        <div className="flex justify-center">
          <div className="relative inline-block max-w-full">
            <img
              src={guide.src}
              alt={t(`piercingBooking.zoneGuide.${guide.altKey}`)}
              className="block max-w-full h-auto max-h-[min(70vh,560px)] rounded-lg"
              loading="lazy"
              decoding="async"
            />
            {hotspot && (
              <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                {hotspot.spots.map((spot, index) => {
                  const r = spot.r ?? 4.2;
                  return (
                    <span
                      key={`${activeId || activeName}-${index}`}
                      className={`piercing-guide-hotspot ${
                        isPreviewOnly
                          ? 'piercing-guide-hotspot--preview'
                          : 'piercing-guide-hotspot--selected'
                      }`}
                      style={{
                        left: `${spot.x}%`,
                        top: `${spot.y}%`,
                        width: `${r * 2}%`,
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </figure>
  );
}
